/**
 * WatchlistBlock - Indicators to monitor with thresholds
 */

import React from 'react'
import { WatchlistBlock as WatchlistBlockType, MetricValue } from '../../../../lib/cockpit/schemas'

interface WatchlistItem {
  id: string
  indicator: string
  description?: string
  current_value?: MetricValue
  threshold?: string
  status?: 'normal' | 'warning' | 'critical'
  maps_to_scenario_state?: string
}

interface WatchlistBlockProps {
  data: WatchlistBlockType['data']
  blockId: string
  compact?: boolean
}

const STATUS_STYLES = {
  normal: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    dot: 'bg-yellow-500',
    text: 'text-yellow-700 dark:text-yellow-400',
  },
  critical: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    dot: 'bg-red-500',
    text: 'text-red-700 dark:text-red-400',
  },
}

function formatValue(value: MetricValue | undefined): string {
  if (value === undefined) return '—'
  if (typeof value === 'number') return value.toLocaleString()
  if (typeof value === 'string') return value
  if (typeof value === 'object' && 'formatted' in value) return value.formatted || String(value.value)
  return String(value)
}

function WatchlistBlock({ data, blockId, compact }: WatchlistBlockProps) {
  if (compact) {
    return (
      <div className="space-y-1">
        {data.items.slice(0, 5).map((item: WatchlistItem) => {
          const status: 'normal' | 'warning' | 'critical' = item.status || 'normal'
          const style = STATUS_STYLES[status]
          
          return (
            <div key={item.id} className="flex items-center gap-2 text-sm">
              <div className={`w-2 h-2 rounded-full ${style.dot}`} />
              <span className="text-gray-700 dark:text-gray-300">{item.indicator}</span>
            </div>
          )
        })}
        {data.items.length > 5 && (
          <div className="text-xs text-gray-500">+{data.items.length - 5} more</div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {data.items.map((item: WatchlistItem) => {
        const status: 'normal' | 'warning' | 'critical' = item.status || 'normal'
        const style = STATUS_STYLES[status]
        
        return (
          <div
            key={item.id}
            className={`p-4 rounded-lg border ${style.bg} ${style.border}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className={`w-3 h-3 rounded-full ${style.dot} mt-1`} />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {item.indicator}
                  </div>
                  {item.description && (
                    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {item.description}
                    </div>
                  )}
                </div>
              </div>
              
              {item.current_value !== undefined && (
                <div className="text-right">
                  <div className="font-mono font-semibold text-gray-900 dark:text-white">
                    {formatValue(item.current_value)}
                  </div>
                  {item.threshold && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Threshold: {item.threshold}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Scenario mapping */}
            {data.show_scenario_mapping && item.maps_to_scenario_state && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Triggers: {item.maps_to_scenario_state}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default WatchlistBlock



